import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";

const AuthCtx = createContext(null);
const STORAGE_KEY = "artibytes_auth_v1";
const TOKEN_KEY = "artibytes_access_token";

function hasPiSdk() {
  return typeof window !== "undefined" && window.Pi && window.Pi.authenticate;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [kyc, setKyc] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUserFromStorage = useCallback(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        if (data?.user?.uid) {
          setUser(data.user);
          console.log("[useAuth] Loaded user from storage:", data.user.username || data.user.uid);
        }
        if (data.accessToken) {
          setAccessToken(data.accessToken);
          console.log("[useAuth] Loaded access token from storage");
        }
        if (typeof data.kyc !== "undefined") {
          setKyc(data.kyc);
        }
      }
      // Fallback to TOKEN_KEY
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) setAccessToken(prev => prev || token);
    } catch (err) {
      console.warn("[useAuth] Failed to load from storage:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial hydration on mount
  useEffect(() => {
    loadUserFromStorage();
  }, [loadUserFromStorage]);

  // Poll for storage changes from App.js authentication
  useEffect(() => {
    const interval = setInterval(() => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const data = JSON.parse(raw);
          const storedUid = data?.user?.uid;
          const currentUid = user?.uid;
          if (storedUid && storedUid !== currentUid) {
            console.log("[useAuth] User changed, reloading from storage");
            loadUserFromStorage();
          }
        } else if (user) {
          setUser(null);
          setAccessToken(null);
          setKyc(null);
        }
      } catch {}
    }, 500);

    const handleStorageChange = (e) => {
      if (e.key === STORAGE_KEY || e.key === TOKEN_KEY || e.key === null) {
        loadUserFromStorage();
      }
    };
    window.addEventListener("storage", handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [loadUserFromStorage, user]);

  const refreshKyc = useCallback(async () => {
    try {
      const status = "verified";
      setKyc(status);
      return status;
    } catch (err) {
      console.warn("refreshKyc failed:", err);
      return kyc ?? null;
    }
  }, [kyc]);

  /**
   * ✅ CORRECT FLOW per Pi Network docs:
   * 1. Try stored token first (fast path — App.js already authenticated)
   * 2. If no stored token (e.g. after logout), call Pi.authenticate directly
   *    Pi.authenticate is safe to call multiple times — silent after first consent
   */
  const signIn = useCallback(async () => {
    console.log("[useAuth] Sign-in triggered");

    // Fast path: use stored token from App.js auth
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        if (data?.user?.uid && data?.accessToken) {
          setUser(data.user);
          setAccessToken(data.accessToken);
          console.log("[useAuth] ✓ Sign-in from storage:", data.user.username);
          await refreshKyc();
          return;
        }
      }
    } catch {}

    // Slow path: no stored token — call Pi.authenticate directly
    // This happens after logout or first visit
    if (!hasPiSdk()) {
      throw new Error("Please open in Pi Browser to sign in");
    }

    console.log("[useAuth] No stored token — calling Pi.authenticate");
    try {
      const authResult = await Promise.race([
        window.Pi.authenticate(["username", "payments"], () => {}),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Auth timeout")), 30000)
        ),
      ]);

      if (!authResult?.accessToken) {
        throw new Error("Authentication failed — no access token");
      }

      const u = authResult.user || {};
      const normalizedUser = {
        uid: u?.uid || u?.uid_v2 || null,
        username: u?.username ? `@${String(u.username).replace(/^@/, "")}` : null,
      };

      const authData = {
        accessToken: authResult.accessToken,
        user: normalizedUser,
        refreshedAt: Date.now(),
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(authData));
      setUser(normalizedUser);
      setAccessToken(authResult.accessToken);
      console.log("[useAuth] ✓ Pi.authenticate succeeded:", normalizedUser.username);
      await refreshKyc();
    } catch (err) {
      console.error("[useAuth] Pi.authenticate failed:", err);
      setUser(null);
      setAccessToken(null);
      setKyc(null);
      throw err;
    }
  }, [refreshKyc]);

  const signOut = useCallback(async () => {
    try {
      setUser(null);
      setAccessToken(null);
      setKyc(null);
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(TOKEN_KEY);
      console.log("[useAuth] Signed out");
    } catch (err) {
      console.warn("signOut warning:", err);
    }
  }, []);

  const value = useMemo(
    () => ({ user, accessToken, kyc, loading, signIn, signOut, refreshKyc }),
    [user, accessToken, kyc, loading, signIn, signOut, refreshKyc]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

export function getAccessToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
}